<template>
  <main class="vuelve-promo">
    <section v-if="promoExpired" class="vuelve-promo__layout">
      <div class="vuelve-promo__header card">
        <p class="vuelve-promo__eyebrow">Promoción caducada</p>
        <h1>Esta promoción ya no está disponible</h1>
        <p>
          La promoción de reactivación era válida hasta 16/06/2026 a las
          23:59. Si necesitas ayuda con tu cuenta, contacta con administración.
        </p>
      </div>
    </section>

    <section v-else-if="submitted" class="vuelve-promo__layout">
      <div class="vuelve-promo__header card">
        <p class="vuelve-promo__eyebrow">Solicitud recibida</p>
        <h1>Gracias, hemos recibido tus datos</h1>
        <p>
          El equipo de administración revisará la solicitud y contactará contigo
          para identificar tu cuenta y ayudarte con el acceso a la promoción.
        </p>
      </div>
    </section>

    <section v-else class="vuelve-promo__layout">
      <header class="vuelve-promo__header card">
        <p class="vuelve-promo__eyebrow">Promoción especial de reactivación</p>
        <h1>Te damos 15 días para volver a probar Eurekers</h1>
        <p>
          Hace un tiempo estuviste con nosotros y queremos ponértelo fácil para
          volver. Déjanos tus datos y administración contactará contigo para
          identificar tu cuenta. Cuando activemos la promoción tendrás 15 días
          de acceso sin coste inicial. Si después quieres continuar, no tendrás
          que hacer nada: al finalizar el periodo tu cuenta pasará al plan Gold
          por un importe de 29,40 €/mes (el pago se realizará trimestralmente).
        </p>
        <p class="vuelve-promo__valid-until">
          Promoción válida hasta 16/06/2026 a las 23:59.
        </p>
      </header>

      <form class="vuelve-promo__form card" @submit.prevent="submit">
        <h2 class="vuelve-promo__section-title">Datos de alta</h2>
        <div class="vuelve-promo__grid">
          <label>
            Email
            <input v-model.trim="form.email" type="email">
            <span v-if="errors.email" class="vuelve-promo__error">{{ errors.email }}</span>
          </label>
          <label>
            Teléfono
            <input v-model.trim="form.telephone" type="tel" inputmode="numeric" maxlength="15">
            <span v-if="errors.telephone" class="vuelve-promo__error">{{ errors.telephone }}</span>
          </label>
          <label>
            CIF/NIF
            <input v-model.trim="form.tax_nif" type="text">
            <span v-if="errors.tax_nif" class="vuelve-promo__error">{{ errors.tax_nif }}</span>
          </label>
          <label>
            Nombre fiscal
            <input v-model.trim="form.tax_name" type="text">
            <span v-if="errors.tax_name" class="vuelve-promo__error">{{ errors.tax_name }}</span>
          </label>
          <label>
            Dirección fiscal
            <input v-model.trim="form.tax_address" type="text">
            <span v-if="errors.tax_address" class="vuelve-promo__error">{{ errors.tax_address }}</span>
          </label>
        </div>

        <fieldset class="vuelve-promo__iban">
          <legend>IBAN: datos bancarios</legend>
          <div class="vuelve-promo__iban-field">
            <span class="vuelve-promo__iban-prefix">ES</span>
            <input
              :value="formattedIbanDigits"
              type="text"
              inputmode="numeric"
              maxlength="27"
              placeholder="XX-XXXX-XXXX-XXXX-XXXX-XXXX"
              aria-label="IBAN español"
              @input="updateIbanDigits($event.target.value)"
              @paste.prevent="pasteIban"
            >
          </div>
        </fieldset>
        <span v-if="errors.iban" class="vuelve-promo__error vuelve-promo__error--block">{{ errors.iban }}</span>

        <label class="vuelve-promo__agreement">
          <input v-model="form.agreement" type="checkbox">
          <span>
            He leído y acepto las condiciones de la promoción, la política de
            privacidad, el aviso legal y la normativa SEPA. Entiendo que esta
            promoción incluye 15 días sin coste inicial y que, si no solicito la
            cancelación antes de que finalice ese periodo desde la activación,
            mi cuenta continuará en el plan Gold por un importe de 29,40 €/mes
            (el pago se realizará trimestralmente).
          </span>
        </label>
        <span v-if="errors.agreement" class="vuelve-promo__error vuelve-promo__error--block">{{ errors.agreement }}</span>

        <p v-if="submitError" class="vuelve-promo__submit-error">{{ submitError }}</p>

        <button class="vuelve-promo__submit" type="submit" :disabled="submitting">
          {{ submitting ? 'Enviando...' : 'Solicitar promoción' }}
        </button>
      </form>
    </section>
  </main>
</template>

<script>
import axios from 'axios';

export default {
  name: 'VuelvePromo',
  data() {
    return {
      submitted: false,
      submitting: false,
      promoEndsAt: new Date('2026-06-16T23:59:59+02:00'),
      submitError: '',
      errors: {},
      form: {
        email: '',
        telephone: '',
        tax_nif: '',
        tax_name: '',
        tax_address: '',
        iban_digits: '',
        agreement: false,
      },
    };
  },
  computed: {
    promoExpired() {
      return new Date() > this.promoEndsAt;
    },
    formattedIbanDigits() {
      return this.formatIbanDigits(this.form.iban_digits);
    },
  },
  mounted() {
    this.redirectToCanonicalHost();
  },
  methods: {
    redirectToCanonicalHost() {
      if (window.location.hostname === 'eurekers.com') {
        window.location.replace(`https://www.eurekers.com${window.location.pathname}${window.location.search}${window.location.hash}`);
      }
    },
    async submit() {
      this.submitting = true;
      this.submitError = '';
      this.errors = {};

      try {
        const response = await axios.post('/api/v1/vuelve-promo/request', {
          email: this.form.email,
          telephone: this.form.telephone,
          tax_nif: this.form.tax_nif,
          tax_name: this.form.tax_name,
          tax_address: this.form.tax_address,
          iban: this.splitIbanDigits(this.form.iban_digits),
          agreement: this.form.agreement,
        });

        if (response.data && response.data.success) {
          this.submitted = true;
          return;
        }

        this.errors = response.data.errors || {};
        this.submitError = response.data.message || 'Revisa los campos del formulario.';
      } catch (error) {
        const data = error.response && error.response.data ? error.response.data : {};
        this.errors = data.errors || {};
        this.submitError = data.message || 'No se ha podido enviar la solicitud.';
      } finally {
        this.submitting = false;
      }
    },
    normalizeIbanDigits(value) {
      const compactText = (value || '').toUpperCase().replace(/[\s-]/g, '');
      const digits = compactText.indexOf('ES') === 0
        ? compactText.slice(2).replace(/\D/g, '')
        : compactText.replace(/\D/g, '');

      return digits.slice(0, 22);
    },
    updateIbanDigits(value) {
      this.form.iban_digits = this.normalizeIbanDigits(value);
    },
    formatIbanDigits(value) {
      const ibanDigits = this.normalizeIbanDigits(value);
      const groups = [
        ibanDigits.slice(0, 2),
        ibanDigits.slice(2, 6),
        ibanDigits.slice(6, 10),
        ibanDigits.slice(10, 14),
        ibanDigits.slice(14, 18),
        ibanDigits.slice(18, 22),
      ];

      return groups.filter(Boolean).join('-');
    },
    splitIbanDigits(value) {
      const ibanDigits = this.normalizeIbanDigits(value);
      const fields = [
        ['iban', 2],
        ['bank', 4],
        ['office', 4],
        ['control', 4],
        ['number', 4],
        ['number_more', 4],
      ];
      let offset = 0;
      const iban = {};

      fields.forEach(([field, length]) => {
        iban[field] = ibanDigits.slice(offset, offset + length);
        offset += length;
      });

      return iban;
    },
    pasteIban(event) {
      const pastedText = event.clipboardData ? event.clipboardData.getData('text') : '';
      this.form.iban_digits = this.normalizeIbanDigits(pastedText);
    },
  },
};
</script>

<style scoped>
.vuelve-promo {
  box-sizing: border-box;
  min-height: 100vh;
  background-color: var(--color-background, #f8f9fa);
  color: var(--color-text-primary, #212529);
  overflow-x: hidden;
  padding: 20px 5%;
}

.vuelve-promo__state {
  max-width: 720px;
  margin: 80px auto;
  font-size: 18px;
  text-align: center;
}

.vuelve-promo__layout {
  box-sizing: border-box;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
}

.card {
  background-color: var(--color-surface, #ffffff);
  border: 1px solid var(--color-outline, #ced4da);
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);
  max-width: 100%;
  padding: 25px 30px;
  width: 100%;
}

.vuelve-promo__header.card {
  margin-bottom: 30px;
}

.vuelve-promo__eyebrow {
  color: var(--color-highlight, #00add8);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  margin: 0 0 8px;
  text-transform: uppercase;
}

.vuelve-promo__header h1 {
  color: var(--color-text-primary, #0f0f1a);
  font-size: 28px;
  font-weight: 600;
  line-height: 1.25;
  margin: 0 0 12px;
}

.vuelve-promo__header p {
  color: var(--color-text-secondary, rgba(0, 0, 0, 0.6));
  max-width: 820px;
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
}

.vuelve-promo__header .vuelve-promo__valid-until {
  color: var(--color-highlight, #00add8);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  margin-top: 12px;
}

.vuelve-promo__section-title {
  border-bottom: 1px solid var(--color-outline-variant, #e0e0e0);
  color: var(--color-text-primary, #212529);
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 25px;
  padding-bottom: 10px;
}

.vuelve-promo__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 20px;
}

.vuelve-promo label,
.vuelve-promo__iban {
  color: var(--color-text-secondary, #6c757d);
  display: flex;
  flex-direction: column;
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
  min-width: 0;
}

.vuelve-promo input[type="text"],
.vuelve-promo input[type="email"],
.vuelve-promo input[type="tel"] {
  background-color: var(--color-input-background, #ffffff);
  border: 1px solid var(--color-outline, #ced4da);
  border-radius: 6px;
  box-sizing: border-box;
  color: var(--color-text-primary, #212529);
  font-size: 16px;
  height: 46px;
  padding: 12px 15px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  min-width: 0;
  width: 100%;
}

.vuelve-promo input[type="text"]:focus,
.vuelve-promo input[type="email"]:focus,
.vuelve-promo input[type="tel"]:focus {
  border-color: var(--color-highlight, #00add8);
  box-shadow: 0 0 0 3px var(--color-primary-alpha, rgba(0, 173, 216, 0.25));
  outline: none;
}

.vuelve-promo input[readonly] {
  background-color: var(--color-muted, #f0f0f0);
  color: var(--color-text-secondary, #6c757d);
  cursor: not-allowed;
}

.vuelve-promo__iban {
  border: 1px solid var(--color-outline, #ced4da);
  border-radius: 6px;
  box-sizing: border-box;
  display: block;
  margin: 25px 0 0;
  min-inline-size: 0;
  padding: 18px;
  width: 100%;
}

.vuelve-promo__iban legend {
  color: var(--color-text-primary, #212529);
  font-size: 14px;
  font-weight: 600;
  padding: 0 6px;
}

.vuelve-promo__iban-field {
  align-items: stretch;
  background-color: var(--color-input-background, #ffffff);
  border: 1px solid var(--color-outline, #ced4da);
  border-radius: 6px;
  display: flex;
  max-width: 100%;
  overflow: hidden;
  width: 100%;
}

.vuelve-promo__iban-field:focus-within {
  border-color: var(--color-highlight, #00add8);
  box-shadow: 0 0 0 3px var(--color-primary-alpha, rgba(0, 173, 216, 0.25));
}

.vuelve-promo__iban-prefix {
  align-items: center;
  color: var(--color-text-secondary, #6c757d);
  display: flex;
  font-weight: 600;
  justify-content: center;
  padding: 0 14px;
  flex: 0 0 auto;
}

.vuelve-promo__iban-field input[type="text"] {
  border: 0;
  border-left: 1px solid var(--color-outline-variant, #e0e0e0);
  border-radius: 0;
  box-shadow: none;
  flex: 1 1 auto;
  height: 46px;
}

.vuelve-promo__iban-field input[type="text"]:focus {
  border-color: var(--color-outline-variant, #e0e0e0);
  box-shadow: none;
}

.vuelve-promo__agreement {
  align-items: flex-start;
  color: var(--color-text-primary, #212529);
  flex-direction: row;
  font-weight: 500;
  gap: 10px;
  line-height: 1.45;
  margin-top: 25px;
  min-width: 0;
}

.vuelve-promo__agreement input {
  accent-color: var(--color-highlight, #00add8);
  margin-top: 3px;
  min-width: 18px;
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}

.vuelve-promo__agreement span {
  flex: 1 1 auto;
  min-width: 0;
}

.vuelve-promo__error {
  color: var(--color-error, #dc3545);
  font-size: 13px;
  font-weight: 600;
}

.vuelve-promo__error--block {
  display: block;
  margin-top: 8px;
}

.vuelve-promo__submit-error {
  color: var(--color-error, #dc3545);
  font-weight: 700;
  margin: 18px 0 0;
}

.vuelve-promo__submit {
  background-color: var(--color-highlight, #00add8);
  border: 1px solid var(--color-highlight, #00add8);
  border-radius: 6px;
  color: var(--color-text-on-highlight, #ffffff);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  margin-top: 25px;
  min-height: 46px;
  padding: 12px 25px;
  text-align: center;
  text-transform: uppercase;
  transition: background-color 0.2s ease, border-color 0.2s ease, filter 0.2s ease;
}

.vuelve-promo__submit:hover {
  background-color: var(--color-primary-dark, #008fb3);
  border-color: var(--color-primary-dark, #008fb3);
}

.vuelve-promo__submit:disabled {
  cursor: default;
  opacity: 0.65;
  filter: grayscale(0.3);
}

@media (max-width: 720px) {
  .vuelve-promo {
    padding: 12px;
  }

  .vuelve-promo__header h1 {
    font-size: 22px;
  }

  .vuelve-promo__header p {
    font-size: 14px;
  }

  .card {
    padding: 16px;
  }

  .vuelve-promo__header.card {
    margin-bottom: 16px;
  }

  .vuelve-promo__section-title {
    font-size: 18px;
    margin-bottom: 18px;
  }

  .vuelve-promo__grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .vuelve-promo__iban {
    margin-top: 20px;
    padding: 14px;
  }

  .vuelve-promo__iban-prefix {
    padding: 0 12px;
  }

  .vuelve-promo__iban-field input[type="text"] {
    font-size: 15px;
  }

  .vuelve-promo__agreement {
    font-size: 14px;
    margin-top: 20px;
  }

  .vuelve-promo__submit {
    width: 100%;
  }
}

@media (max-width: 380px) {
  .vuelve-promo {
    padding: 10px;
  }

  .card {
    padding: 14px;
  }

  .vuelve-promo__iban {
    padding: 12px;
  }

  .vuelve-promo__iban-field input[type="text"] {
    padding-left: 12px;
    padding-right: 12px;
  }
}
</style>
